import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useIsMobile from "components/hooks/useIsMobile";
import useIsDesktop from "components/hooks/useIsDesktop";
import FlexPage from "components/layout/FlexPage";
import { Heart } from "lucide-react";
import { useMemberDetails } from "components/context/MemberDetailsContext";
import { CurrentUserContext } from "components/context/RequireAuth";
import { planFlagsFromStatus } from "utils/memberFamilyPlan";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import useTitle from "components/hooks/useTitle";

import {
  useListAllTaskInspirations,
  useListAllMemberTaskInspirations,
  useListAllTaskInspirationCategories,
  useListAllMemberTaskDiscoveryInspirationCategories,
  useCreateMemberTaskWishlistEntry,
  useListMemberTaskWishlistEntries,
  useUpdateMemberTaskWishlistEntry,
} from "services/tasks";
import { TaskInspiration, TaskInspirationCategory } from "protogen/tasks_pb";
import { LazyIcon } from "../common/iconUtils";
import LinkRouter from "components/navigation/LinkRouter";
import { taskDiscoveryAddTaskClause } from "common/memberModuleResolver";

const FEATURED_CATEGORY_TITLE = "featured";

const featuredFirst = (
  categories: TaskInspirationCategory[],
): TaskInspirationCategory[] => {
  const isFeatured = (category: TaskInspirationCategory) =>
    category.title.trim().toLowerCase() === FEATURED_CATEGORY_TITLE;
  return [
    ...categories.filter(isFeatured),
    ...categories.filter((category) => !isFeatured(category)),
  ];
};

const TaskCategoryQuickLink = ({
  title,
  onClick,
  itemsPerRow = 5,
}: {
  title: string;
  onClick: () => void;
  itemsPerRow?: number;
}) => {
  const isMobile = useIsMobile();
  return (
    <Typography
      variant="bodyHeavy"
      sx={{
        display: "flex",
        padding: isMobile ? "12px" : "16px",
        border: "1px solid #D4D4D4",
        borderRadius: "12px",
        alignItems: "center",
        textWrap: isMobile ? "nowrap" : "wrap",

        width: isMobile
          ? "auto"
          : `calc(100% * (1/${itemsPerRow}) - 12px + 2px)`,
      }}
      onClick={onClick}
    >
      {title}
    </Typography>
  );
};

const TaskCategoryQuickLinks = ({
  categories,
  onClick,
  maxItems = 10,
}: {
  categories: TaskInspirationCategory[];
  onClick: (ref: string) => void;
  maxItems?: number;
}) => {
  const isMobile = useIsMobile();
  // On mobile, we just always show all the quick links.
  const [showMore, setShowMore] = useState(isMobile);
  const excludedQuickLinks = ["Popular", "Seasonal"];
  const filteredItems = categories.filter((c) => {
    return !excludedQuickLinks.includes(c.title);
  });
  const includeShowMore = !showMore && filteredItems.length > maxItems;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: isMobile ? "nowrap" : "wrap",
          gap: "12px",
          marginBottom: "8px",
          whiteSpace: "nowrap",
          overflowX: "auto",

          // Full width scrolling
          paddingLeft: isMobile ? "20px" : "",
          paddingRight: isMobile ? "20px" : "",
          marginLeft: isMobile ? "-20px" : "",
          marginRight: isMobile ? "-20px" : "",
          msOverflowStyle: "none" /* Hide scrollbar */,
          scrollbarWidth: "none" /* Hide scrollbars */,
          cursor: "pointer",
        }}
      >
        {filteredItems.map((category, i) => {
          if (includeShowMore && i >= maxItems) return null;
          return (
            <TaskCategoryQuickLink
              key={category.ref}
              title={category.title}
              onClick={() => onClick(category.ref)}
            />
          );
        })}
      </Box>
      {includeShowMore && (
        <Button
          variant="text"
          onClick={() => setShowMore(true)}
          sx={{ height: "unset", alignSelf: "start" }}
        >
          Show all categories
        </Button>
      )}
    </Box>
  );
};

const TaskCategorySection = ({
  category,
  taskInspirations,
  onTaskClick,
  onWishlistClick,
  isTaskWishlisted,
  showWishlistButton = false,
}: {
  category: TaskInspirationCategory;
  taskInspirations: TaskInspiration[];
  onTaskClick: (task: TaskInspiration) => void;
  onWishlistClick?: (title: string) => void;
  isTaskWishlisted?: (title: string) => boolean;
  showWishlistButton?: boolean;
}) => {
  const filteredTaskInspirations = taskInspirations.filter((task) => {
    return task.categoryRef === category.ref && task.status === "active";
  });
  const isDesktop = useIsDesktop();
  const Icon = LazyIcon(category.iconName);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: "16px",
      }}
      key={category.ref}
    >
      {/* Example tasks container */}
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: isDesktop ? "1fr 1fr" : "1fr",
          gridGap: "12px",
        }}
      >
        {/* Category name */}
        <Box
          sx={{
            display: "flex",
            background: "linear-gradient(343deg, #E8F8F8, #EDF8F6)",
            padding: "24px 20px",
            borderRadius: "12px",
            gridRow: "1 / span 2",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Icon width="32" height="32" stroke="#198282" />
          <Typography variant="h3">{category.title}</Typography>
        </Box>
        {filteredTaskInspirations.map((task) => {
          const isWishlisted = isTaskWishlisted?.(task.title);
          return (
            /* Example task */
            <Box
              sx={{
                border: "1px solid #D4D4D4",
                padding: "12px 20px",
                borderRadius: "8px",
                alignContent: "center",
                display: "flex",
                gap: "8px",
                justifyContent: "space-between",
                alignItems: "center",
              }}
              key={task.ref}
            >
              <Typography variant="body" sx={{ width: "100%" }}>
                {task.title}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                  alignItems: "center",
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onTaskClick(task)}
                  sx={{
                    height: "32px",
                  }}
                >
                  Add
                </Button>
                {showWishlistButton && onWishlistClick && (
                  <IconButton
                    size="small"
                    onClick={() => onWishlistClick(task.title)}
                    sx={{}}
                  >
                    <Heart
                      size={24}
                      {...(isWishlisted
                        ? {
                            fill: "#EF7B77",
                            stroke: "#EF7B77",
                          }
                        : {
                            stroke: "#8E9598",
                          })}
                    />
                  </IconButton>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ({
  accountType = "advisor",
}: {
  accountType?: "advisor" | "member";
}) => {
  useTitle("Task Discovery");
  const navigate = useNavigate();
  const { data: memberDetails } = useMemberDetails();
  const currentUser = useContext(CurrentUserContext);
  const memberPlanFlags = planFlagsFromStatus(currentUser.familyStatus);
  const memberPageDescription = (
    <>
      Here are our most popular tasks to help you get the most out of Faye.{" "}
      {taskDiscoveryAddTaskClause(memberPlanFlags)}, or use the{" "}
      <IconButton
        size="small"
        onClick={() => navigate("/tasks#wishlist")}
        sx={{ padding: "0px", marginBottom: "3px" }}
      >
        <Heart size={16} />
      </IconButton>{" "}
      to save it for{" "}
      <LinkRouter
        to="/tasks#wishlist"
        sx={{
          color: "#198282",
        }}
        inline
      >
        later
      </LinkRouter>
      .
    </>
  );
  const advisorPageDescription =
    "Here are our most popular tasks to help your families get the most out of Faye.";
  const isMobile = useIsMobile();
  const { pushSnack } = useSnackpack();
  const sectionRefs = useRef<{
    [key: string]: React.RefObject<HTMLDivElement>;
  }>({});
  const [wishlistEntries, setWishlistEntries] = useState<any[]>([]);
  const [pendingWishlistTasks, setPendingWishlistTasks] = useState<Set<string>>(
    new Set(),
  );

  const taskInspirationRequestFn =
    accountType === "advisor"
      ? useListAllTaskInspirations
      : useListAllMemberTaskInspirations;

  const { data, request: listAllTaskInspirationRequest } =
    taskInspirationRequestFn(() => {});

  const taskInspirationCategoriesRequestFn =
    accountType === "advisor"
      ? useListAllTaskInspirationCategories
      : useListAllMemberTaskDiscoveryInspirationCategories;

  const {
    data: inspirationCategories,
    request: listAllTaskInspirationCategoriesRequest,
  } = taskInspirationCategoriesRequestFn(() => {});
  const orderedCategories = useMemo(
    () => featuredFirst(inspirationCategories?.taskInspirationCategories || []),
    [inspirationCategories],
  );
  const { request: createWishlistEntry } = useCreateMemberTaskWishlistEntry();
  const { request: listWishlistEntriesRequest } =
    useListMemberTaskWishlistEntries();
  const { request: updateWishlistEntryRequest } =
    useUpdateMemberTaskWishlistEntry();
  const fetchWishlistEntries = async () => {
    if (accountType === "member") {
      const resp = await listWishlistEntriesRequest({
        states: ["active"],
      });
      if (resp) {
        setWishlistEntries(resp.taskWishlistEntries);
      }
    }
  };

  useEffect(() => {
    listAllTaskInspirationCategoriesRequest();
    listAllTaskInspirationRequest();
    fetchWishlistEntries();
  }, []);

  orderedCategories.forEach((category) => {
    if (!sectionRefs.current[category.ref]) {
      sectionRefs.current[category.ref] = React.createRef();
    }
  });

  const handleQuickLinkClick = (ref: string) => {
    sectionRefs.current[ref]?.current?.scrollIntoView({ behavior: "smooth" });
  };

  const wishListSupported = accountType === "member";
  const handleTaskClick = (task: TaskInspiration) => {
    if (accountType === "advisor") {
      navigate("/tasks/add", { state: { inspiredTask: task } });
    } else if (accountType === "member") {
      const wishlistEntry = wishlistEntries.find(
        (entry) => entry.title === task.title,
      );
      navigate("/tasks/add", {
        state: {
          inspiredTask: task,
          family: memberDetails?.family,
          ...(wishlistEntry && { taskWishlistEntry: wishlistEntry }),
        },
      });
    }
  };

  const handleWishlistClick = async (title: string) => {
    if (!wishListSupported) return;

    const existingEntry = wishlistEntries.find(
      (entry) => entry.title === title,
    );

    if (existingEntry) {
      // Unheart - remove from wishlist
      setPendingWishlistTasks((prev) => new Set(prev).add(title));

      await updateWishlistEntryRequest({
        ref: existingEntry.ref,
        taskRef: "",
        state: "deleted",
      });

      // Remove from local state
      setWishlistEntries((prev) =>
        prev.filter((entry) => entry.ref !== existingEntry.ref),
      );

      pushSnack(`"${title}" removed from wishlist`);
    } else {
      // Heart - add to wishlist
      setPendingWishlistTasks((prev) => new Set(prev).add(title));

      const resp = await createWishlistEntry({ title });
      if (resp) {
        setWishlistEntries((prev) => [...prev, resp.taskWishlistEntry]);
        pushSnack({
          message: "Task saved!",
          key: resp.taskWishlistEntry?.ref || "",
          action: {
            label: "View wishlist",
            href: "/tasks#wishlist",
          },
        });
      }
    }

    // Remove from pending state regardless of success/failure
    setPendingWishlistTasks((prev) => {
      const newSet = new Set(prev);
      newSet.delete(title);
      return newSet;
    });
  };

  const isTaskWishlisted = (title: string) => {
    return (
      wishlistEntries.some((entry) => entry.title === title) ||
      pendingWishlistTasks.has(title)
    );
  };
  const onAddTaskClick = () => {
    navigate("/tasks/add", { state: { family: memberDetails?.family } });
  };
  return (
    <FlexPage
      leftAligned
      sx={{
        margin:
          accountType === "advisor" && isMobile
            ? "0px"
            : accountType === "advisor"
              ? "64px"
              : "auto",
        padding:
          accountType === "advisor" && isMobile
            ? "32px 24px"
            : isMobile
              ? "32px 24px"
              : accountType !== "advisor" && !isMobile
                ? "64px 32px"
                : "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? "20px" : "32px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <Typography variant="display">Task discovery</Typography>
            <Typography variant="body" sx={{ maxWidth: "550px" }}>
              {accountType === "advisor"
                ? advisorPageDescription
                : memberPageDescription}
            </Typography>
          </Box>
          {!isMobile && (
            <Button sx={{ minWidth: "120px" }} onClick={onAddTaskClick}>
              New task
            </Button>
          )}
        </Box>

        {/* Category quick links */}
        <TaskCategoryQuickLinks
          categories={orderedCategories}
          onClick={handleQuickLinkClick}
        />
        {/* Category page sections */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: isMobile ? "48px" : "64px",
          }}
        >
          {orderedCategories.map((category) => {
            return (
              <Box
                key={category.ref}
                ref={sectionRefs.current[category.ref]}
                sx={{
                  scrollMarginTop: "64px",
                }}
              >
                <TaskCategorySection
                  category={category}
                  taskInspirations={data?.taskInspirations || []}
                  onTaskClick={handleTaskClick}
                  onWishlistClick={handleWishlistClick}
                  isTaskWishlisted={isTaskWishlisted}
                  showWishlistButton={wishListSupported}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    </FlexPage>
  );
};
